import { Box, Button, ChevronDown, ChevronUp, Text } from '../../../../../mads';
import { LinearGradient } from '../../../../../shared/components/LinearGradient/linear-gradient';
import { useI18n } from '../../../../../shared/I18n';

import { colors } from '@indeed/ifl-tokens';
import React, { useCallback, useRef, useState } from 'react';
// eslint-disable-next-line @typescript-eslint/no-restricted-imports
import { AccessibilityInfo, findNodeHandle, type View, type ViewStyle } from 'react-native';

const fadeStyle: ViewStyle = {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    height: 50,
};

function VerticalFade() {
    return (
        <LinearGradient
            colors={['rgba(255, 255, 255, 0.0)', 'rgba(255, 255, 255, 1.0)']}
            start={{ x: 0, y: 0 }}
            end={{ x: 0, y: 1 }}
            style={fadeStyle}
            pointerEvents="none"
        />
    );
}

export type HiddenPreviewProps = {
    previewContent: React.ReactNode;
    revealContent: React.ReactNode;
    revealAccessibilityLabel?: string;
    isExpanded?: boolean;
    onExpandedChange?: (expanded: boolean) => void;
    showMoreLabel?: string;
    showLessLabel?: string;
};

export function HiddenPreview({
    previewContent,
    revealContent,
    revealAccessibilityLabel,
    isExpanded: controlledExpanded,
    onExpandedChange,
    showMoreLabel,
    showLessLabel,
}: HiddenPreviewProps) {
    const { gettext } = useI18n();
    const [internalExpanded, setInternalExpanded] = useState(false);
    const revealRef = useRef<View>(null);
    const hasFocusedRef = useRef(false);

    const resolvedShowMoreLabel = showMoreLabel ?? gettext('Show more');
    const resolvedShowLessLabel = showLessLabel ?? gettext('Show less');

    const isExpanded = controlledExpanded ?? internalExpanded;

    const handleToggle = useCallback(() => {
        const next = !isExpanded;
        if (!next) {
            hasFocusedRef.current = false;
        }
        if (onExpandedChange) {
            onExpandedChange(next);
        } else {
            setInternalExpanded(next);
        }
    }, [isExpanded, onExpandedChange]);

    const handleRevealLayout = useCallback(() => {
        if (!hasFocusedRef.current && revealRef.current) {
            const node = findNodeHandle(revealRef.current);
            if (node) {
                hasFocusedRef.current = true;
                AccessibilityInfo.setAccessibilityFocus(node);
            }
        }
    }, []);

    return (
        <Box>
            {!isExpanded ? (
                <>
                    <Box sx={{ position: 'relative' }}>
                        {previewContent}
                        <VerticalFade />
                    </Box>

                    <Button
                        variant="text"
                        iconAfter={<ChevronDown color={colors.foreground.primary['[base]']} />}
                        onPress={handleToggle}
                        ariaLabel={resolvedShowMoreLabel}
                    >
                        <Text>{resolvedShowMoreLabel}</Text>
                    </Button>
                </>
            ) : (
                <>
                    <Box
                        ref={revealRef}
                        accessible={revealAccessibilityLabel !== undefined ? true : undefined}
                        accessibilityLabel={revealAccessibilityLabel}
                        onLayout={handleRevealLayout}
                    >
                        {revealContent}
                    </Box>

                    <Button
                        variant="text"
                        iconAfter={<ChevronUp color={colors.foreground.primary['[base]']} />}
                        onPress={handleToggle}
                        ariaLabel={resolvedShowLessLabel}
                    >
                        <Text>{resolvedShowLessLabel}</Text>
                    </Button>
                </>
            )}
        </Box>
    );
}
